/**
 * ML noise suppression for the outgoing microphone track.
 *
 * getUserMedia's own `noiseSuppression: true` (see getLocalStream in webrtc.js)
 * is the browser's built-in denoiser — decent in Chrome, weak or absent in
 * Safari and Firefox, and not something a user can turn off if it artifacts on
 * their setup. This runs RNNoise (a small RNN trained for exactly this) in an
 * AudioWorklet, in-process, on top of whatever the browser already did.
 *
 * The wasm and the compiled worklet processor are static assets copied from
 * @sapphi-red/web-noise-suppressor's dist/ into public/noise-suppressor/ at
 * dependency-update time (see public/noise-suppressor/README.txt) rather than
 * imported through the bundler, because they need to be fetched by URL —
 * `addModule` for the worklet and `fetch` for the wasm — and CRA's webpack
 * config (unejected) has no loader that turns either into a servable URL.
 *
 * The package itself is imported lazily, inside createDenoisedTrack rather
 * than at module scope: its classes are declared as `extends AudioWorkletNode`,
 * which throws the moment the module evaluates in any environment (jsdom in
 * particular, i.e. every component test that merely imports CallContext.js)
 * that doesn't define that global — real browsers only.
 */

const ASSET_BASE = `${process.env.PUBLIC_URL}/noise-suppressor`;

// Fetched once for the life of the tab and reused for every call and every
// toggle — it's ~150KB and identical every time, so there's nothing to gain
// from re-fetching it per AudioContext.
let wasmBinaryPromise = null;
const getWasmBinary = (loadRnnoise) => {
    if (!wasmBinaryPromise) {
        wasmBinaryPromise = loadRnnoise({
            url: `${ASSET_BASE}/rnnoise.wasm`,
            simdUrl: `${ASSET_BASE}/rnnoise_simd.wasm`
        });
    }
    return wasmBinaryPromise;
};

export const isNoiseSuppressionSupported = () =>
    typeof window !== 'undefined' &&
    typeof window.AudioContext !== 'undefined' &&
    typeof window.AudioWorkletNode !== 'undefined';

/**
 * Wrap a raw microphone track with an RNNoise graph and hand back the cleaned
 * track plus how to tear the graph down.
 *
 * Built fresh per call (and disposed at teardown) rather than kept open across
 * calls: an AudioContext holds real OS audio resources, and there's no reason
 * to keep one alive between calls where nothing is being sent anyway.
 *
 * RNNoise assumes 48kHz frames; the AudioContext is forced to that rate
 * rather than left on the device default (44100Hz on plenty of laptops),
 * which would otherwise resample every frame into and out of the wrong size.
 */
export const createDenoisedTrack = async (rawTrack) => {
    const { RnnoiseWorkletNode, loadRnnoise } = await import('@sapphi-red/web-noise-suppressor');
    const ctx = new AudioContext({ sampleRate: 48000 });

    let source;
    let denoiser;
    let destination;
    try {
        const [, wasmBinary] = await Promise.all([
            ctx.audioWorklet.addModule(`${ASSET_BASE}/rnnoiseWorkletProcessor.js`),
            getWasmBinary(loadRnnoise)
        ]);

        source = ctx.createMediaStreamSource(new MediaStream([rawTrack]));
        denoiser = new RnnoiseWorkletNode(ctx, { maxChannels: 1, wasmBinary });
        destination = ctx.createMediaStreamDestination();
        source.connect(denoiser).connect(destination);
    } catch (err) {
        await ctx.close().catch(() => { /* already unusable */ });
        throw err;
    }

    const [outputTrack] = destination.stream.getAudioTracks();

    return {
        track: outputTrack,
        dispose: () => {
            source.disconnect();
            denoiser.destroy();
            denoiser.disconnect();
            destination.disconnect();
            outputTrack.stop();
            ctx.close().catch(() => { /* worklet may already be gone */ });
        }
    };
};
